import {
  FORMAS_PAGAMENTO,
  type FormaPagamento,
  type LancamentoDetalhe,
  type PagamentoDetalhe,
} from '@/features/financeiro/api/financeiroApi';
import type { OrcamentoDetalhe } from '@/features/orcamentos/api/orcamentosApi';

type Props = {
  lancamento: LancamentoDetalhe;
  pagamento: PagamentoDetalhe;
  orcamento: OrcamentoDetalhe | null;
  nomeEmpresa: string;
  /** Ordinal da baixa dentro do histórico (1, 2, 3…). */
  ordem: number;
  totalBaixas: number;
};

const moeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const dataFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dataHoraFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const rotuloForma: Record<FormaPagamento, string> = Object.fromEntries(
  FORMAS_PAGAMENTO.map((f) => [f.id, f.label]),
) as Record<FormaPagamento, string>;

export function ReciboBaixaA4({
  lancamento,
  pagamento,
  orcamento,
  nomeEmpresa,
  ordem,
  totalBaixas,
}: Props) {
  const pecas = orcamento?.itens.filter((i) => i.tipo === 'Produto') ?? [];
  const servicos = orcamento?.itens.filter((i) => i.tipo === 'Servico') ?? [];

  const saldoAposBaixa = (() => {
    const ativos = lancamento.pagamentos
      .filter((p) => !p.estornado)
      .filter((p) => new Date(p.data).getTime() <= new Date(pagamento.data).getTime());
    const pagoAteAqui = ativos.reduce((acc, p) => acc + p.valor, 0);
    return Math.max(0, Math.round((lancamento.valor - pagoAteAqui) * 100) / 100);
  })();

  return (
    <div className="recibo-baixa-a4">
      <header className="recibo-baixa-a4__cabecalho">
        <div>
          <h1 className="recibo-baixa-a4__empresa">{nomeEmpresa}</h1>
          <div className="recibo-baixa-a4__sub">
            Recibo de {lancamento.tipo === 'Receber' ? 'Recebimento' : 'Pagamento'}
          </div>
        </div>
        <div className="recibo-baixa-a4__num">
          <div className="recibo-baixa-a4__num-rotulo">Baixa</div>
          <div className="recibo-baixa-a4__num-valor">
            {ordem}/{totalBaixas}
          </div>
          <div className="recibo-baixa-a4__sub">
            {dataHoraFmt.format(new Date(pagamento.data))}
          </div>
        </div>
      </header>

      <section className="recibo-baixa-a4__secao">
        <h2 className="recibo-baixa-a4__secao-titulo">Pagamento</h2>
        <div className="recibo-baixa-a4__grid">
          <Campo rotulo="Valor da baixa" valor={moeda.format(pagamento.valor)} destaque />
          <Campo
            rotulo="Forma"
            valor={pagamento.formaPagamento ? rotuloForma[pagamento.formaPagamento] : '—'}
          />
          <Campo rotulo="Data" valor={dataFmt.format(new Date(pagamento.data))} />
          <Campo
            rotulo="Status"
            valor={pagamento.estornado ? 'ESTORNADO' : 'Confirmado'}
          />
        </div>
        {pagamento.observacao && (
          <div className="recibo-baixa-a4__obs">
            <div className="recibo-baixa-a4__campo-rotulo">Observação da baixa</div>
            <div className="recibo-baixa-a4__obs-texto">{pagamento.observacao}</div>
          </div>
        )}
        {pagamento.estornado && (
          <div className="recibo-baixa-a4__obs recibo-baixa-a4__obs--alerta">
            <div className="recibo-baixa-a4__campo-rotulo">Estorno</div>
            <div className="recibo-baixa-a4__obs-texto">
              {pagamento.estornadoEm
                ? `Estornado em ${dataHoraFmt.format(new Date(pagamento.estornadoEm))}`
                : 'Estornado'}
              {pagamento.estornadoMotivo ? ` — ${pagamento.estornadoMotivo}` : ''}
            </div>
          </div>
        )}
      </section>

      <section className="recibo-baixa-a4__secao">
        <h2 className="recibo-baixa-a4__secao-titulo">Lançamento</h2>
        <div className="recibo-baixa-a4__grid">
          <Campo rotulo="Descrição" valor={lancamento.descricao} />
          <Campo
            rotulo={lancamento.tipo === 'Receber' ? 'Cliente' : 'Fornecedor'}
            valor={
              lancamento.tipo === 'Receber'
                ? lancamento.clienteNome ?? '—'
                : lancamento.fornecedor ?? lancamento.clienteNome ?? '—'
            }
          />
          <Campo rotulo="Valor total" valor={moeda.format(lancamento.valor)} />
          <Campo rotulo="Já pago" valor={moeda.format(lancamento.valorPago)} />
          <Campo
            rotulo="Saldo após esta baixa"
            valor={moeda.format(saldoAposBaixa)}
            destaque
          />
          <Campo rotulo="Vencimento" valor={dataFmt.format(new Date(lancamento.vencimento))} />
        </div>
      </section>

      {orcamento && (
        <>
          <section className="recibo-baixa-a4__secao">
            <h2 className="recibo-baixa-a4__secao-titulo">
              Ordem de serviço {orcamento.numero}
            </h2>
            <div className="recibo-baixa-a4__grid">
              <Campo rotulo="Cliente" valor={orcamento.clienteNome} />
              <Campo
                rotulo={orcamento.placa ? 'Placa' : 'Veículo'}
                valor={orcamento.veiculoDescricao}
              />
              <Campo
                rotulo="Responsável"
                valor={orcamento.colaboradorResponsavelNome ?? '—'}
              />
              <Campo rotulo="Data" valor={dataFmt.format(new Date(orcamento.data))} />
              <Campo rotulo="Total da ordem de serviço" valor={moeda.format(orcamento.total)} />
            </div>
          </section>

          {servicos.length > 0 && (
            <SecaoItens titulo="Serviços realizados" itens={servicos} />
          )}
          {pecas.length > 0 && (
            <SecaoItens titulo="Peças aplicadas" itens={pecas} />
          )}
        </>
      )}

      <footer className="recibo-baixa-a4__rodape">
        <div className="recibo-baixa-a4__assinatura">
          <div className="recibo-baixa-a4__linha-assinatura" />
          <div>
            {lancamento.tipo === 'Receber'
              ? lancamento.clienteNome ?? 'Cliente'
              : lancamento.fornecedor ?? 'Recebedor'}
          </div>
        </div>
        <div className="recibo-baixa-a4__assinatura">
          <div className="recibo-baixa-a4__linha-assinatura" />
          <div>{nomeEmpresa}</div>
        </div>
      </footer>
    </div>
  );
}

function Campo({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: string;
  destaque?: boolean;
}) {
  return (
    <div className="recibo-baixa-a4__campo">
      <div className="recibo-baixa-a4__campo-rotulo">{rotulo}</div>
      <div
        className={`recibo-baixa-a4__campo-valor${
          destaque ? ' recibo-baixa-a4__campo-valor--destaque' : ''
        }`}
      >
        {valor}
      </div>
    </div>
  );
}

function SecaoItens({
  titulo,
  itens,
}: {
  titulo: string;
  itens: OrcamentoDetalhe['itens'];
}) {
  return (
    <section className="recibo-baixa-a4__secao">
      <h2 className="recibo-baixa-a4__secao-titulo">{titulo}</h2>
      <table className="recibo-baixa-a4__tabela">
        <thead>
          <tr>
            <th>Descrição</th>
            <th>Responsável</th>
            <th className="recibo-baixa-a4__num-col">Qtd</th>
            <th className="recibo-baixa-a4__num-col">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {itens.map((i) => (
            <tr key={i.id}>
              <td>{i.descricao}</td>
              <td>{i.colaboradorNome ?? '—'}</td>
              <td className="recibo-baixa-a4__num-col">{i.quantidade}</td>
              <td className="recibo-baixa-a4__num-col">{moeda.format(i.subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
